import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'

export interface FloatPosition {
  x: number
  y: number
}

export type DockSide = 'left' | 'right'

export interface AssistiveFloatPersist {
  x: number
  y: number
  docked: boolean
  dockSide: DockSide
}

const DRAG_THRESHOLD = 8

function clampPosition(pos: FloatPosition, floatSize: number, bottomOffset: number): FloatPosition {
  const maxX = Math.max(8, window.innerWidth - floatSize - 8)
  const maxY = Math.max(8, window.innerHeight - floatSize - bottomOffset)
  return {
    x: Math.min(Math.max(8, pos.x), maxX),
    y: Math.min(Math.max(8, pos.y), maxY),
  }
}

function nearestDockSide(pos: FloatPosition, floatSize: number): DockSide {
  return pos.x + floatSize / 2 >= window.innerWidth / 2 ? 'right' : 'left'
}

export function getFloatingPanelStyle(
  position: FloatPosition,
  floatSize: number,
  panelWidth = 370,
  panelHeight = 560,
): {
  position: 'fixed'
  left: number
  top: number
  width: number
  height: number
  zIndex: number
} {
  const width = Math.min(panelWidth, window.innerWidth - 32)
  const height = Math.min(panelHeight, window.innerHeight - 110)
  let left = position.x + floatSize - width
  left = Math.min(Math.max(8, left), window.innerWidth - width - 8)
  let top = position.y - height - 12
  if (top < 8) {
    top = Math.min(position.y + floatSize + 12, window.innerHeight - height - 8)
  }
  top = Math.max(8, top)
  return {
    position: 'fixed',
    left,
    top,
    width,
    height,
    zIndex: 9999,
  }
}

export interface UseAssistiveFloatOptions {
  storageKey: string
  floatSize: number
  bottomOffset?: number
  getDefaultPosition?: (floatSize: number, bottomOffset: number) => FloatPosition
}

export function useAssistiveFloat({
  storageKey,
  floatSize,
  bottomOffset = 24,
  getDefaultPosition,
}: UseAssistiveFloatOptions) {
  const defaultPos = useCallback(() => {
    if (getDefaultPosition) return getDefaultPosition(floatSize, bottomOffset)
    return {
      x: Math.max(8, window.innerWidth - floatSize - 20),
      y: Math.max(8, window.innerHeight - floatSize - bottomOffset - 20),
    }
  }, [bottomOffset, floatSize, getDefaultPosition])

  const [position, setPosition] = useState<FloatPosition>(() => defaultPos())
  const [isDocked, setIsDocked] = useState(false)
  const [dockSide, setDockSide] = useState<DockSide>('right')

  const dragRef = useRef({
    active: false,
    moved: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    pointerId: -1,
  })
  const onTapRef = useRef<(() => void) | null>(null)

  const persist = useCallback((payload: AssistiveFloatPersist) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(payload))
    } catch {
      // ignore quota errors
    }
  }, [storageKey])

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey)
      if (!raw) {
        const next = clampPosition(defaultPos(), floatSize, bottomOffset)
        setPosition(next)
        return
      }
      const parsed = JSON.parse(raw) as AssistiveFloatPersist
      setPosition(clampPosition({ x: parsed.x, y: parsed.y }, floatSize, bottomOffset))
      setIsDocked(Boolean(parsed.docked))
      setDockSide(parsed.dockSide === 'left' ? 'left' : 'right')
    } catch {
      setPosition(clampPosition(defaultPos(), floatSize, bottomOffset))
    }
  }, [storageKey, floatSize, bottomOffset, defaultPos])

  useEffect(() => {
    const onResize = () => {
      setPosition((prev) => clampPosition(prev, floatSize, bottomOffset))
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [floatSize, bottomOffset])

  const dock = useCallback(() => {
    const side = nearestDockSide(position, floatSize)
    setDockSide(side)
    setIsDocked(true)
    persist({ x: position.x, y: position.y, docked: true, dockSide: side })
  }, [floatSize, persist, position])

  const undock = useCallback(() => {
    setIsDocked(false)
    persist({ x: position.x, y: position.y, docked: false, dockSide })
  }, [dockSide, persist, position])

  const bindTap = useCallback((handler: () => void) => {
    onTapRef.current = handler
  }, [])

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (isDocked) return
    dragRef.current = {
      active: true,
      moved: false,
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
      pointerId: event.pointerId,
    }
    event.currentTarget.setPointerCapture(event.pointerId)
  }, [isDocked, position.x, position.y])

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (!dragRef.current.active || dragRef.current.pointerId !== event.pointerId || isDocked) return
    const dx = event.clientX - dragRef.current.startX
    const dy = event.clientY - dragRef.current.startY
    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
      dragRef.current.moved = true
    }
    setPosition(clampPosition({
      x: dragRef.current.originX + dx,
      y: dragRef.current.originY + dy,
    }, floatSize, bottomOffset))
  }, [bottomOffset, floatSize, isDocked])

  const onPointerUp = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (!dragRef.current.active || dragRef.current.pointerId !== event.pointerId || isDocked) return
    const wasDrag = dragRef.current.moved
    dragRef.current.active = false
    event.currentTarget.releasePointerCapture(event.pointerId)
    const finalPos = clampPosition({
      x: dragRef.current.originX + (event.clientX - dragRef.current.startX),
      y: dragRef.current.originY + (event.clientY - dragRef.current.startY),
    }, floatSize, bottomOffset)
    setPosition(finalPos)
    persist({ x: finalPos.x, y: finalPos.y, docked: false, dockSide })
    if (!wasDrag) {
      onTapRef.current?.()
    }
  }, [bottomOffset, dockSide, floatSize, isDocked, persist])

  return {
    position,
    isDocked,
    dockSide,
    dock,
    undock,
    bindTap,
    dragHandlers: { onPointerDown, onPointerMove, onPointerUp },
  }
}
